import {
  useState, useEffect, useMemo, useCallback,
} from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { useAnalytics } from 'use-analytics';
import _ from 'lodash';
import { setFlightTripSearchInputs } from '../../../../../store/slices/tripSearch';
import CheckboxSelectFilter from '../../../../Filters/CheckboxSelectFilter';
import flightSearchInput from '../../../../../helpers/selectors/flightSearchInput';
import { updateFlightSearchObj } from '../../../../../store/slices/flight';

const AgentPreferencesFilter = ({ tripId, variant, filterBgColor }) => {
  const isTripSearch = !tripId;
  const { searchInputs } = useSelector(flightSearchInput(tripId, isTripSearch));
  const { travelAgency, isLoggedIn } = useSelector((state) => state.user);
  const doHidePreferredFlightRateCodes = travelAgency && travelAgency.filterSettings
  && travelAgency.filterSettings.doHidePreferredFlightRateCodes;
  const hideNdcFaresPreferenceLabel = travelAgency?.filterSettings?.hideNdcFaresPreferenceLabel || 'Hide NDC fares';

  const [selectedPreferences, setSelectedPreferences] = useState([]);
  const dispatch = useDispatch();
  const { track } = useAnalytics();

  const getFilteredSetting = useCallback((filterSettings, key, cfpKey) => {
    if (!filterSettings) return false;
    const value = isLoggedIn ? filterSettings?.[key] : filterSettings?.[cfpKey];
    return !!value;
  }, [isLoggedIn]);

  const doHideNdcPreference = useMemo(() => getFilteredSetting(travelAgency?.filterSettings,
    'doHideNdcPreferenceCheckbox', 'doHideNdcPreferenceCheckboxForCFP'), [getFilteredSetting, travelAgency]);

  const doHideNdcFaresDefault = useMemo(() => getFilteredSetting(travelAgency?.filterSettings,
    'doHideNdcFares', 'doHideNdcFaresForCFP'), [getFilteredSetting, travelAgency]);

  const doEnableFlightsPolling = travelAgency?.settings?.doEnableFlightsPolling;

  const preferences = [];
  if (searchInputs
    && searchInputs.rateCodes
    && searchInputs.rateCodes.length && !doHidePreferredFlightRateCodes) {
    preferences.push(
      { value: 1, label: 'Only show flights with preferred flight rate codes' },
    );
  }
  preferences.push(
    { value: 2, label: 'Only show refundable flights' },
  );
  preferences.push(
    { value: 3, label: 'Show long connect flights' },
  );
  if (!doHideNdcPreference) {
    preferences.push(
      { value: 4, label: hideNdcFaresPreferenceLabel },
    );
  }
  preferences.push(
    {
      value: 5,
      label: (
        <>
          Show more flight options (may take longer)
          <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-blue-100 text-blue-800 border border-blue-600">
            BETA
          </span>
        </>
      ),
      plainTextLabel: 'Show more flight options',
    },
  );

  useEffect(() => {
    if (isTripSearch && searchInputs.doHideNdcFares === undefined) {
      dispatch(setFlightTripSearchInputs({ doHideNdcFares: doHideNdcFaresDefault }));
    } else if (!isTripSearch && searchInputs.doHideNdcFares === undefined) {
      dispatch(updateFlightSearchObj({
        tripId,
        searchObj: { doHideNdcFares: doHideNdcFaresDefault },
      }));
    }
  }, [dispatch, doHideNdcFaresDefault, isTripSearch, searchInputs.doHideNdcFares, tripId]);

  useEffect(() => {
    if (searchInputs.doEnableFlightsPolling === undefined && doEnableFlightsPolling) {
      const payload = { doEnableFlightsPolling: true };
      if (isTripSearch) {
        dispatch(setFlightTripSearchInputs(payload));
      } else {
        dispatch(updateFlightSearchObj({
          tripId,
          searchObj: payload,
        }));
      }
    }
  }, [dispatch, isTripSearch, doEnableFlightsPolling, tripId, searchInputs.doEnableFlightsPolling]);

  useEffect(() => {
    const selectedPref = [];
    if (searchInputs.rateCodes && searchInputs.rateCodes.length
      && searchInputs.exactMatchRateCodesOnly) {
      selectedPref.push(1);
    }
    if (searchInputs.doShowRefundableFlightsOnly) {
      selectedPref.push(2);
    }
    if (searchInputs.doShowLongConnectFlights) {
      selectedPref.push(3);
    }
    if (searchInputs.doHideNdcFares) {
      selectedPref.push(4);
    }
    if (searchInputs.doEnableFlightsPolling) {
      selectedPref.push(5);
    }
    setSelectedPreferences(selectedPref);
  }, [searchInputs.exactMatchRateCodesOnly, searchInputs.rateCodes, searchInputs.doHideNdcFares,
    searchInputs.doShowRefundableFlightsOnly, searchInputs.doShowLongConnectFlights,
    searchInputs.doEnableFlightsPolling]);

  const handleChange = (newValue) => {
    const exactMatchRateCodesOnly = newValue && _.find(newValue, (v) => v === 1) !== undefined;
    const payload = {
      tripId,
      searchObj: {
        page: 1,
        exactMatchRateCodesOnly: exactMatchRateCodesOnly ? true : undefined,
        doShowRefundableFlightsOnly: newValue && _.find(newValue, (v) => v === 2) !== undefined,
        doShowLongConnectFlights: newValue && _.find(newValue, (v) => v === 3) !== undefined,
        doHideNdcFares: newValue && _.find(newValue, (v) => v === 4) !== undefined,
        doEnableFlightsPolling: newValue && _.find(newValue, (v) => v === 5) !== undefined,
      },
    };

    if (isTripSearch) dispatch(setFlightTripSearchInputs({ ...payload.searchObj }));
    else dispatch(updateFlightSearchObj(payload));
    track('agentPreferencesFilter.handleChange', {
      filterPreferences: JSON.stringify(newValue),
    });
  };

  return preferences && preferences.length > 0 ? (
    <CheckboxSelectFilter
      options={preferences}
      values={selectedPreferences}
      onChange={handleChange}
      label="Preferences"
      widthInside="max-w-85"
      variant={isTripSearch ? undefined : variant}
      bgClassName={!isTripSearch ? '' : filterBgColor}
      checkboxStyles={{ padding: '0 9px 0 9px' }}
      checkboxGroupClasses="gap-y-2"
    />
  ) : <></>;
};

AgentPreferencesFilter.propTypes = {
  tripId: PropTypes.string,
  variant: PropTypes.string,
  filterBgColor: PropTypes.string,
};

AgentPreferencesFilter.defaultProps = {
  filterBgColor: undefined,
  tripId: undefined,
  variant: 'outline',
};
export default AgentPreferencesFilter;
